// Libraries
import React from 'react';

// Supermove
import {Space} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {AccountGrant, UserModel} from '@supermove/models';
import {colors} from '@supermove/styles';
import {Datetime} from '@supermove/utils';

import ErrorCallout from '@shared/design/components/Callout/ErrorCallout';
import Text from '@shared/design/components/Text';

// App

interface ExpiredPrimaryAccountBannerProps {
  viewer: UserModel;
  spacingAboveIfVisible?: number;
  spacingBelowIfVisible?: number;
  handleConnectAccount: () => void;
}

const getExpiredInfo = (expiredStatusUpdatedAt: string) => {
  const dateExpired = Datetime.convertToDisplayDatetime(
    expiredStatusUpdatedAt,
    Datetime.DISPLAY_SHORT_DATE,
  );
  const timeExpired = Datetime.convertToDisplayDatetime(
    expiredStatusUpdatedAt,
    Datetime.DISPLAY_TIME,
  );
  return {dateExpired, timeExpired};
};

const getCompanyAccountExpiredText = (
  expiredCompanyAccountStatusUpdatedAt: string,
  hasEditPermissions: boolean,
) => {
  const {dateExpired} = getExpiredInfo(expiredCompanyAccountStatusUpdatedAt);
  return `The primary company account was disconnected on ${dateExpired}. ${hasEditPermissions ? `Reconnect the company email account to continue sending emails with the company account.` : 'Contact your admin to reconnect the email account.'} `;
};

const ErrorBannerMessage = ({
  text,
  hasEditPermissions,
  connectAccountGrant,
  spacingAbove,
  spacingBelow,
}: {
  text: string;
  hasEditPermissions: boolean;
  connectAccountGrant: () => void;
  spacingAbove?: number;
  spacingBelow?: number;
}) => {
  return (
    <React.Fragment>
      {spacingAbove ? <Space height={spacingAbove} /> : null}
      <ErrorCallout>
        <Text.Body style={{color: colors.red.warning}}>
          {text}
          {hasEditPermissions ? (
            <React.Fragment>
              {' '}
              <Text.Link onPress={connectAccountGrant}>Click to reconnect</Text.Link>
            </React.Fragment>
          ) : (
            ''
          )}
        </Text.Body>
      </ErrorCallout>
      {spacingBelow ? <Space height={spacingBelow} /> : null}
    </React.Fragment>
  );
};

const ExpiredPrimaryAccountBanner = ({
  viewer,
  spacingAboveIfVisible,
  spacingBelowIfVisible,
  handleConnectAccount,
}: ExpiredPrimaryAccountBannerProps) => {
  const {hasEditPermissions, hasExpiredCompanyAccount, expiredCompanyAccountStatusUpdatedAt} =
    AccountGrant.accountGrantInfo(viewer);

  if (hasExpiredCompanyAccount) {
    return (
      <ErrorBannerMessage
        text={getCompanyAccountExpiredText(
          expiredCompanyAccountStatusUpdatedAt ?? '',
          hasEditPermissions,
        )}
        hasEditPermissions={hasEditPermissions}
        connectAccountGrant={handleConnectAccount}
        spacingAbove={spacingAboveIfVisible}
        spacingBelow={spacingBelowIfVisible}
      />
    );
  }

  return null;
};

// --------------------------------------------------
// Data
// --------------------------------------------------
ExpiredPrimaryAccountBanner.fragment = gql`
  ${AccountGrant.accountGrantInfo.fragment}

  fragment ExpiredPrimaryAccountBanner on User {
    id
    ...AccountGrant_accountGrantInfo
  }
`;

export default ExpiredPrimaryAccountBanner;
